import { Alert, Box } from '@mui/material';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { StatusRenderer } from '../../../components/AgTable/cell-renderers/StatusRenderer';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { Loader } from '../../../components/Loader/Loader';
import { EmailAuditRow } from '../../../schemas/EmailAudit.schema';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { DateFormattersId } from '../../../util/formatters/DateFormatters';
import { useRequestEmailAudits } from '../../../services/queries-hooks/RequestEmailAuditsQueriesHooks';
import { V2RequestType } from './company-period-request.view-model';
import { CompanyPeriodRequestManageViewModel } from './company-period-request.view-model';
import { emailAuditStatusDisplay } from './email-audit-display';
import { collapseToLatestPerRecipient, deriveEmailAuditNote } from './email-audit-notes';
import { formatRequestPeriodLabel } from './manage-request-utils';

export interface EmailStatusModalProps {
  open: boolean;
  request: CompanyPeriodRequestManageViewModel | null;
  onClose: () => void;
}

function EmailAuditStatusCellRenderer(params: ICellRendererParams<EmailAuditRow>) {
  if (!params.data) {
    return null;
  }
  const { status, label } = emailAuditStatusDisplay(params.data.emailStatus);
  return <StatusRenderer value={label} status={status} />;
}

export function EmailStatusModal({ open, request, onClose }: EmailStatusModalProps) {
  const requestSource = request?.requestType as V2RequestType | undefined;
  const { data, isLoading, isError } = useRequestEmailAudits(request?.id, requestSource, open);

  const { rows, hasOnlySkipRows } = useMemo(() => collapseToLatestPerRecipient(data ?? []), [data]);

  const colDefs = useMemo<ColDef<EmailAuditRow>[]>(() => {
    const dateFmt = AgFormatterService.get().getFormatterForId<EmailAuditRow, string | null>(
      DateFormattersId.date
    );
    // Fixed widths (no flex) so a long Notes reason can push the grid wider
    // than the modal and surface AG Grid's horizontal scrollbar instead of
    // being truncated to fit. The Notes tooltip shows the full text on hover.
    return [
      { headerName: 'Recipient', field: 'recipientEmail', width: 260 },
      {
        headerName: 'Type',
        field: 'recipientType',
        width: 80,
        valueFormatter: (p: ValueFormatterParams<EmailAuditRow, string>) => (p.value === 'cc' ? 'Cc' : 'To'),
      },
      {
        headerName: 'Status',
        field: 'emailStatus',
        width: 140,
        cellRenderer: EmailAuditStatusCellRenderer,
      },
      {
        headerName: 'Sent',
        field: 'emailSentAt',
        width: 120,
        valueFormatter: dateFmt,
      },
      {
        headerName: 'Notes',
        width: 480,
        valueGetter: (p) => (p.data ? deriveEmailAuditNote(p.data) : ''),
        tooltipValueGetter: (p) => (p.data ? deriveEmailAuditNote(p.data) : ''),
      },
    ];
  }, []);

  const title = request
    ? `Email delivery — ${request.companyName}, ${formatRequestPeriodLabel(request)}`
    : 'Email delivery';

  return (
    <BasicDialog open={open} onClose={onClose} title={title} fullWidth maxWidth='md' lightDismiss>
      <Box sx={{ padding: '1rem 1.5rem 1.5rem' }}>
        {isLoading ? (
          <Loader height='12rem' />
        ) : isError ? (
          <Alert severity='error'>Could not load email delivery details. Please try again.</Alert>
        ) : hasOnlySkipRows ? (
          <Alert severity='info'>
            No email was sent for this request — it was covered by a universal request email sent the same
            day.
          </Alert>
        ) : rows.length === 0 ? (
          <Alert severity='info'>No emails have been sent for this request yet.</Alert>
        ) : (
          <Box sx={{ height: '24rem' }}>
            <AgTable
              rowData={rows}
              columnDefs={colDefs}
              rowGroupPanelShow='never'
              suppressMovableColumns
              tooltipShowDelay={500}
            />
          </Box>
        )}
      </Box>
    </BasicDialog>
  );
}
